import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApi } from '@/api/client';

/**
 * API keys data layer — programmatic-access tokens for the VNETWORK OpenAPI.
 * Endpoints live on the v2 service backend (`/v2/apikeys`); this is the only
 * version, so it's the one we port.
 */

export interface ApiKey {
  id: string;
  name: string;
  /** Masked/partial key as listed; the full secret is only returned on resolve/create. */
  key: string;
  created_at: string;
  /** Last-used timestamp, or null if never used. */
  issued_at: string | null;
}

export const apiKeyKeys = {
  list: ['account', 'api-keys'] as const,
};

export function useApiKeys() {
  return useQuery<ApiKey[]>({
    queryKey: apiKeyKeys.list,
    queryFn: async () => {
      const res = await serviceApi.get('/v2/apikeys');
      return res.data?.data?.apikeys ?? [];
    },
    staleTime: 30_000,
  });
}

function useApiKeyInvalidation() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: apiKeyKeys.list });
}

/** Create a key. Returns the full secret — shown once and never retrievable again. */
export function useCreateApiKey(
  options?: UseMutationOptions<string, unknown, string>,
) {
  const invalidate = useApiKeyInvalidation();
  return useMutation({
    mutationFn: async (name: string) => {
      const res = await serviceApi.post('/v2/apikeys', { name });
      // The create response shape varies; the full secret may arrive as
      // data.key (string), data.key.key (nested), data.apikey (matches the
      // resolve endpoint) or data.secret. Read whichever is present so the
      // one-time reveal box is never empty.
      const d = res.data?.data as
        | { key?: string | { key?: string }; apikey?: string; secret?: string }
        | undefined;
      const raw =
        (typeof d?.key === 'string' ? d.key : d?.key?.key) ?? d?.apikey ?? d?.secret;
      // Coerce to string: some backend shapes put the key *object*
      // (id/name/created_at/issued_at) where the secret is expected. Never let a
      // non-string reach the reveal box — React can't render an object child.
      return typeof raw === 'string' ? raw : '';
    },
    ...options,
    onSuccess: (...args) => {
      invalidate();
      options?.onSuccess?.(...args);
    },
  });
}

/** Resolve a key id to its full secret (used to copy an existing key). */
export function useResolveApiKey(
  options?: UseMutationOptions<string, unknown, string>,
) {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await serviceApi.post('/v2/apikeys/resolve', { id });
      return (res.data?.data?.apikey ?? '') as string;
    },
    ...options,
  });
}

export function useRevokeApiKey(
  options?: UseMutationOptions<void, unknown, string>,
) {
  const invalidate = useApiKeyInvalidation();
  return useMutation({
    mutationFn: async (id: string) => {
      await serviceApi.delete(`/v2/apikeys/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      invalidate();
      options?.onSuccess?.(...args);
    },
  });
}
